import { useState, useMemo } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ExternalLink, Loader2, Search, Check, X, ArrowUpDown, ArrowUp, ArrowDown, RotateCcw, Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import type { Database } from "@/integrations/supabase/types";

type SignalStatus = Database["public"]["Enums"]["signal_status"];
type Json = Database["public"]["Tables"]["community_signals"]["Row"]["extracted_claim"];

interface ExtractedClaim {
  deal_type_hint?: string;
  numeric?: string[];
  threshold?: string[];
  region?: string | null;
}

interface CommunitySignal {
  id: string;
  source_url: string;
  title: string;
  body_snippet: string | null;
  author: string | null;
  published_at: string | null;
  extracted_merchant: string | null;
  extracted_claim: Json;
  deal_start_date: string | null;
  deal_end_date: string | null;
  deal_date_confidence: string;
  signal_score: number;
  status: string;
  created_at: string;
  promotion_status?: string | null;
  promoted_source_id?: string | null;
  community_sources?: {
    name: string;
    source_type: string;
  };
}

interface Props {
  signals: CommunitySignal[];
  isLoading: boolean;
}

type SortField = "merchant" | "claim" | "deal_date" | "source" | "published" | "score" | "status";
type SortDirection = "asc" | "desc";

export function CommunitySignalsList({ signals, isLoading }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [promotingSignal, setPromotingSignal] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>("published");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const handleStatusUpdate = async (signalId: string, newStatus: SignalStatus) => {
    setUpdatingStatus(signalId);
    try {
      const { error } = await supabase
        .from("community_signals")
        .update({ status: newStatus })
        .eq("id", signalId);

      if (error) throw error;
      
      queryClient.invalidateQueries({ queryKey: ["community-signals"] });
      toast({ title: `Marked as ${newStatus.toLowerCase()}` });
    } catch (error) {
      toast({
        title: "Error updating status",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    } finally {
      setUpdatingStatus(null);
    }
  };

  // CR074: Promote signal to Merchant Sources
  const handlePromoteToSources = async (signal: CommunitySignal) => {
    setPromotingSignal(signal.id);
    try {
      const response = await supabase.functions.invoke('promote-forum-to-sources', {
        body: { candidate_id: signal.id }
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      const result = response.data;
      
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ["community-signals"] });
        toast({ 
          title: "Added to Merchant Sources",
          description: `Source type: Blog/News • Merchant: ${result.merchant_id}`
        });
      } else {
        // Handle specific error codes
        const errorMessages: Record<string, string> = {
          'FAILED_DUPLICATE_SOURCE': 'Source already exists for this merchant',
          'FAILED_MISSING_MERCHANT': 'Candidate missing merchant',
          'FAILED_MISSING_URL': 'Candidate missing source URL',
          'FAILED_INVALID_URL': 'URL is invalid',
          'FAILED_NOT_APPROVED': 'Signal must be marked as Reviewed first',
          'ALREADY_PROMOTED': 'Already promoted to Merchant Sources',
          'FAILED_DB': 'Database error'
        };
        
        toast({ 
          title: errorMessages[result.error_code] || 'Promotion failed',
          description: result.error_message,
          variant: "destructive"
        });
        
        // Refresh to show updated promotion_status
        queryClient.invalidateQueries({ queryKey: ["community-signals"] });
      }
    } catch (error) {
      toast({
        title: "Error promoting to sources",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    } finally {
      setPromotingSignal(null);
    }
  };

  const getPromotionBadge = (signal: CommunitySignal) => {
    if (signal.promotion_status === 'PROMOTED') {
      return <Badge className="bg-emerald-600 text-xs">Promoted</Badge>;
    }
    if (signal.promotion_status?.startsWith('FAILED_')) {
      return <Badge variant="destructive" className="text-xs">Failed</Badge>;
    }
    return null;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-CA", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined
    });
  };

  const formatDealDate = (startDate: string | null, endDate: string | null) => {
    if (!startDate && !endDate) return "—";
    if (startDate === endDate || !endDate) {
      return formatDate(startDate);
    }
    return `${formatDate(startDate)} – ${formatDate(endDate)}`;
  };

  const getScoreBadge = (score: number) => {
    if (score >= 0.7) return <Badge className="bg-green-500">{(score * 100).toFixed(0)}%</Badge>;
    if (score >= 0.4) return <Badge className="bg-yellow-500">{(score * 100).toFixed(0)}%</Badge>;
    return <Badge variant="secondary">{(score * 100).toFixed(0)}%</Badge>;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "NEW":
        return <Badge className="bg-blue-500">New</Badge>;
      case "REVIEWED":
        return <Badge className="bg-green-500">Reviewed</Badge>;
      case "DISMISSED":
        return <Badge variant="secondary">Dismissed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getDealTypeBadge = (claim: Json) => {
    const parsed = claim as ExtractedClaim | null;
    const hint = parsed?.deal_type_hint;
    if (!hint || hint === "UNKNOWN") return null;
    const colors: Record<string, string> = {
      BONUS: "bg-emerald-500",
      DISCOUNT: "bg-amber-500",
      POINTS: "bg-purple-500"
    };
    return <Badge className={colors[hint] || "bg-gray-500"}>{hint}</Badge>;
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    return sortDirection === "asc" 
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  // Filter and sort signals
  const filteredAndSortedSignals = useMemo(() => {
    const filtered = signals.filter(signal => {
      const matchesSearch = searchQuery === "" || 
        signal.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        signal.extracted_merchant?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === "all" || signal.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });

    return filtered.sort((a, b) => {
      const direction = sortDirection === "asc" ? 1 : -1;
      
      switch (sortField) {
        case "merchant": {
          const merchantA = a.extracted_merchant || "zzz";
          const merchantB = b.extracted_merchant || "zzz";
          return merchantA.localeCompare(merchantB) * direction;
        }

        case "claim": {
          const claimA = (a.extracted_claim as ExtractedClaim | null)?.deal_type_hint || "zzz";
          const claimB = (b.extracted_claim as ExtractedClaim | null)?.deal_type_hint || "zzz";
          return claimA.localeCompare(claimB) * direction;
        }

        case "deal_date": {
          const dateA = a.deal_start_date || "";
          const dateB = b.deal_start_date || "";
          return dateA.localeCompare(dateB) * direction;
        }

        case "source": {
          const sourceA = a.community_sources?.source_type || "";
          const sourceB = b.community_sources?.source_type || "";
          return sourceA.localeCompare(sourceB) * direction;
        }

        case "published": {
          const pubA = a.published_at || a.created_at;
          const pubB = b.published_at || b.created_at;
          return pubA.localeCompare(pubB) * direction;
        }

        case "score":
          return (a.signal_score - b.signal_score) * direction;

        case "status": {
          const statusOrder = { NEW: 0, REVIEWED: 1, DISMISSED: 2 };
          const orderA = statusOrder[a.status as keyof typeof statusOrder] ?? 3;
          const orderB = statusOrder[b.status as keyof typeof statusOrder] ?? 3;
          return (orderA - orderB) * direction;
        }

        default:
          return 0;
      }
    });
  }, [signals, searchQuery, statusFilter, sortField, sortDirection]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by title or merchant..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="NEW">New</SelectItem>
            <SelectItem value="REVIEWED">Reviewed</SelectItem>
            <SelectItem value="DISMISSED">Dismissed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Signals Table */}
      {filteredAndSortedSignals.length === 0 ? (
        <p className="text-muted-foreground text-center py-8">
          {signals.length === 0 
            ? "No signals yet. Configure sources and run ingestion to get started."
            : "No signals match your filters."}
        </p>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">
                  <button className="flex items-center hover:text-foreground" onClick={() => handleSort("merchant")}>
                    Merchant {getSortIcon("merchant")}
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center hover:text-foreground" onClick={() => handleSort("claim")}>
                    Claim {getSortIcon("claim")}
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center hover:text-foreground" onClick={() => handleSort("deal_date")}>
                    Deal Date {getSortIcon("deal_date")}
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center hover:text-foreground" onClick={() => handleSort("source")}>
                    Source {getSortIcon("source")}
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center hover:text-foreground" onClick={() => handleSort("published")}>
                    Published {getSortIcon("published")}
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center hover:text-foreground" onClick={() => handleSort("score")}>
                    Score {getSortIcon("score")}
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center hover:text-foreground" onClick={() => handleSort("status")}>
                    Status {getSortIcon("status")}
                  </button>
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedSignals.map((signal) => (
                <TableRow key={signal.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">
                        {signal.extracted_merchant || "Unknown"}
                      </div>
                      <div className="text-xs text-muted-foreground truncate max-w-[180px]">
                        {signal.title}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {getDealTypeBadge(signal.extracted_claim)}
                      {(signal.extracted_claim as ExtractedClaim | null)?.numeric?.slice(0, 2).map((n, i) => (
                        <Badge key={i} variant="outline">{n}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDealDate(signal.deal_start_date, signal.deal_end_date)}
                    {signal.deal_date_confidence !== "UNKNOWN" && (
                      <span className="text-xs text-muted-foreground ml-1">
                        ({signal.deal_date_confidence.toLowerCase()})
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <a 
                      href={signal.source_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 group"
                    >
                      <Badge variant="outline" className="group-hover:bg-primary/10 transition-colors">
                        {signal.community_sources?.source_type || "—"}
                      </Badge>
                      <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDate(signal.published_at)}
                  </TableCell>
                  <TableCell>
                    {getScoreBadge(signal.signal_score)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {getStatusBadge(signal.status)}
                      {getPromotionBadge(signal)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {/* CR074: Promote to Merchant Sources - only show for REVIEWED signals not yet promoted */}
                      {signal.status === "REVIEWED" && signal.promotion_status !== 'PROMOTED' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handlePromoteToSources(signal)}
                          disabled={promotingSignal === signal.id || !signal.extracted_merchant}
                          title={signal.extracted_merchant 
                            ? "Promote to Merchant Sources" 
                            : "Cannot promote: no merchant extracted"}
                          className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                        >
                          {promotingSignal === signal.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      {signal.status !== "NEW" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleStatusUpdate(signal.id, "NEW")}
                          disabled={updatingStatus === signal.id}
                          title="Reset to New"
                        >
                          {updatingStatus === signal.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RotateCcw className="h-4 w-4 text-blue-500" />
                          )}
                        </Button>
                      )}
                      {signal.status !== "REVIEWED" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleStatusUpdate(signal.id, "REVIEWED")}
                          disabled={updatingStatus === signal.id}
                          title="Mark as Reviewed"
                        >
                          {updatingStatus === signal.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4 text-green-500" />
                          )}
                        </Button>
                      )}
                      {signal.status !== "DISMISSED" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleStatusUpdate(signal.id, "DISMISSED")}
                          disabled={updatingStatus === signal.id}
                          title="Dismiss"
                        >
                          {updatingStatus === signal.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <X className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        asChild
                      >
                        <a href={signal.source_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
